import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ListIcon from "@mui/icons-material/List";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { Box, Stack, Grid, Button, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import { getButtonStyles } from "../../../components/featureButtonStyles/buttonStyles";

export default function Tabs() {
    const navigate = useNavigate();
    const location = useLocation();
    const scrollRef = useRef(null);

    const [activePath, setActivePath] = useState(location.pathname);
    const [expanded, setExpanded] = useState("Appointments");

    const sections = [
        {
            label: "Appointments",
            routes: APP_ROUTES.APPOINTMENTS,
            icon: <EventIcon />
        },
        {
            label: "Assign Slots",
            routes: APP_ROUTES.ASSIGN_SLOTS,
            icon: <AssignmentIcon />
        },
        {
            label: "Slots",
            routes: APP_ROUTES.SLOTS_TIMINGS,
            icon: <AccessTimeIcon />
        }
    ];

    const knownPaths = new Set(
        sections.flatMap((s) => [s.routes.LIST, s.routes.CREATION])
    );

    // Sync active tab on route change
    useEffect(() => {
        if (!knownPaths.has(location.pathname)) {
            navigate(APP_ROUTES.SLOTS_TIMINGS.LIST, { replace: true });
            setActivePath(APP_ROUTES.SLOTS_TIMINGS.LIST);
            setExpanded("Slots");
        } else {
            setActivePath(location.pathname);
            const match = sections.find(
                (s) =>
                    location.pathname === s.routes.LIST ||
                    location.pathname === s.routes.CREATION
            );
            if (match) setExpanded(match.label);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (path) => {
        navigate(path);
    }

    useEffect(() => {
        setActivePath(location.pathname);
        const match = sections.find(
            (s) =>
                location.pathname === s.routes.LIST ||
                location.pathname === s.routes.CREATION
        );
        if (match) setExpanded(match.label);
    }, [location.pathname]);

    return (
        <Box>
            {/* Header */}
            <Box display="flex" alignItems="center" gap="8px" mt={2} mb={2}>
                <CorporateFareIcon sx={{ color: "var(--primary)" }} />
                <Typography variant="h6" fontWeight={800}>
                    Appointment Management
                </Typography>
            </Box>

            <Grid container alignItems="center" spacing={1} sx={{ mt: 2, mb: 2 }}>
                <Grid item size={{ xs: 12, md: 12 }}>
                    <Box
                        ref={scrollRef}
                        sx={{
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            scrollBehavior: "smooth",
                            width: "100%",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                            {sections.map((section, idx) => {
                                const isSectionActive =
                                    activePath === section.routes.LIST ||
                                    activePath === section.routes.CREATION;
                                return (
                                    <Box key={idx} sx={{ display: "inline-block", textAlign: "center" }}>
                                        <Button
                                            key={section.label}
                                            variant="outlined"
                                            onClick={() => handleClick(section.routes.LIST)} // always go to List
                                            sx={getButtonStyles(isSectionActive)}
                                            startIcon={section.icon}
                                        >
                                            {section.label}
                                        </Button>
                                    </Box>
                                )
                            })}
                        </Stack>
                    </Box>
                </Grid>
            </Grid>

            {/* Actions for the expanded section */}
            {sections.map(
                (section) =>
                    expanded === section.label && (
                        <Box
                            key={`${section.label}-actions`}
                            display="flex"
                            gap="12px"
                            pl={2}
                        >
                            <Button
                                variant="outlined"
                                onClick={() => handleClick(section.routes.LIST)}
                                sx={getButtonStyles(activePath === section.routes.LIST, "bottom")}
                                startIcon={<ListIcon />}
                            >
                                List
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => handleClick(section.routes.CREATION)}
                                sx={getButtonStyles(activePath === section.routes.CREATION, "bottom")}
                                startIcon={<PersonAddIcon />}
                            >
                                Creation
                            </Button>
                        </Box>
                    )
            )}
        </Box>
    );
}
